import EventEmitter from "../helpers/EventEmitter"
import EventObserver from "../helpers/EventObserver"

import {clamp} from "../helpers/MathUtils"
import env from 'src/config/env'


let debugSwipeUI = false
let lockScrollIfFullScreen = true
let lockScrollIfFacebookVideo = true

export const UI_MODES = {
  //GUESSING - eg webkit: facebook, messenger, instagram, ios-chrome
  //reliable only once a full ui-swipe-up has occurred in current orientation (min max window.innerHeights are initially unknown)
  GUESSING:"GUESSING",

  //VH_LOCKED - (ios-safari, android-chrome): vh, 100% are unchanging(per orientation), vh = max-content-height
  //reliable
  VH_LOCKED:"VH_LOCKED",

  //FB_VIDEO - a separate browser that uses a split screen video-header/ content
  //not reliable on android, reliable on ios
  FB_VIDEO:"FB_VIDEO",

  //IGNORE - desktop
  //not reliable / not required
  IGNORE:"IGNORE"
}

export const EVENTS = {
  UI_SHOWING:'uiShowing', //bool
  UI_HEIGHT:'uiHeight'
}


//using browser sniff for stability - cant then safely assume browsers are swipeable
export const VH_LOCKED_BROWSER = (env.ios_safari || env.android_chrome)
export const UI_SWIPEABLE_BROWSER = (env.ios_safari || env.android_chrome || env.ios_chrome )
export const FB_VIDEO_BROWSER = (env.facebook_video_ad)



class SwipeableUIService {
  get UI_MODES(){return UI_MODES}
  get EVENTS(){return EVENTS}
  get VH_LOCKED_BROWSER(){return VH_LOCKED_BROWSER}
  get UI_SWIPEABLE_BROWSER(){return UI_SWIPEABLE_BROWSER}
  get FB_VIDEO_BROWSER(){return FB_VIDEO_BROWSER}


  constructor() {
    Object.assign(this, EventEmitter)
    Object.assign(this, EventObserver)


    this.containerEl = document.body
    //this.containerEl = document.getElementById("app")

    this._heights ={
      portrait:{min:100000, max:0},
      landscape:{min:100000, max:0}
    }
    this._touchStartY=0
    this.html = document.querySelector('html')

    this._makeRulers()
    if (debugSwipeUI){ this._setupDebug()}
    this.onFrame()

    this.setupScrollLockBehaviour()

  }

  setupScrollLockBehaviour(){
    // -will lock if touch start is below 100px, and (reliable)uiShowing = false - allows for header pull down
    if (lockScrollIfFullScreen){
      this.listen(this.containerEl,"touchstart", this.onTouchStart, {passive:false, useCapture:false})
      this.listen(this.containerEl,"touchend", this.onTouchEnd, {passive:false, useCapture:false})
      this.listen(this.containerEl,"touchmove", this.onTouchMove, {passive:false, useCapture:false})
      //this.listen(this.containerEl,"scroll", this.onTrapScrollArea, {passive:false})
      this.listen(window,"scroll", this.onScroll, {passive:false})
      //NOTE-for any internally scrolling elements add touchmove-listener with: e.stopPropagation();
    }
  }

  get minContentHeight(){return this._getOrientHeights.min}
  get maxContentHeight(){return this._getOrientHeights.max}
  get maxUIHeight(){return clamp(this._getOrientHeights.max -  this._getOrientHeights.min, 0, 300)}

  get scrollY(){return window.scrollY}
  get vh(){return this.el_vh.clientHeight /*css 100vh*/ }
  get perc_h(){return this.el_perc.clientHeight /*css 100%*/ }
  get sh(){
    let max = Math.max(window.screen.width , window.screen.height)
    let min = Math.min(window.screen.width , window.screen.height)
    return (this.landscape ) ? min : max
  }
  get wh(){return window.innerHeight}
  get ww(){return window.innerWidth}
  get landscape(){
    if (env.ios && env.facebook ){return false}
    return this.ww > this.wh
  }

  get uiMode(){
    if (FB_VIDEO_BROWSER) {return UI_MODES.FB_VIDEO}
    if (!env.mobile){ return UI_MODES.IGNORE}
    if (env.facebook && !env.facebook_video_ad){ return UI_MODES.IGNORE}
    return (VH_LOCKED_BROWSER) ? UI_MODES.VH_LOCKED : UI_MODES.GUESSING
  }

  get uiHeight(){
    if (this.uiMode === UI_MODES.IGNORE){return 0}
    if (this.uiMode === UI_MODES.VH_LOCKED){return this.vh - this.wh}
    if (this.uiMode === UI_MODES.FB_VIDEO){return 0}
    return this.maxContentHeight - this.wh
  }

  get uiSwipable(){
    return UI_SWIPEABLE_BROWSER
  }

  get swipeProgressEstimate(){
    //WARNING: android-chrome does not update wh during swipe/scroll => (1 or 0)
    if (this.uiMode === UI_MODES.IGNORE){return 0}
    if (this.uiMode === UI_MODES.FB_VIDEO){return 0}
    return  1.00 -(this.uiHeight /(this.maxUIHeight || 0.001))
  }

  get uiShowing(){
    if (this.uiMode === UI_MODES.IGNORE){return false}
    if (this.uiMode === UI_MODES.VH_LOCKED){return this.vh > this.wh}
    if (this.uiMode === UI_MODES.FB_VIDEO){return false}
    return (this.maxContentHeight > this.wh)
  }

  destroy() {
    this.unlisten()
    this.off()
  }

  onFrame = () => {
    this._updateContentHeight()
    if (debugSwipeUI){this._updateDebugUIMessage()}
    window.requestAnimationFrame(this.onFrame)

    this.emitIfChanged("uiShowing", EVENTS.UI_SHOWING)
    this.emitIfChanged("uiHeight", EVENTS.UI_HEIGHT)
  }


  /*--UTILS-------------------------------------------------------------------------*/
  get _headerDragging(){
    if (this.uiMode === UI_MODES.FB_VIDEO){return true}
    return (this._touchStartY < 70)
  }

  get fbVideoFullscreen(){
    if (this.uiMode !== UI_MODES.FB_VIDEO){return false}
    if (!lockScrollIfFacebookVideo){return false}
    let max= (env.ios) ? 25 : 2; //ios scrolls to 23 while in splitscreen
    return (window.scrollY >= max)
  }

  get canLockTouch(){
    if (this.uiMode === UI_MODES.FB_VIDEO){return true}

    return (
      lockScrollIfFullScreen &&
      !this.uiShowing &&
      this.VH_LOCKED_BROWSER
    )
  }

  //EVENT UTILS
  bubbling(e){return  (e.eventPhase && e.eventPhase === Event.BUBBLING_PHASE)}

  checkEventPath(e){
    this.scrollingChild = false
    let path =  e.path;

    if (!path){
      path = [];
      var node = e.target;
      while(node != document.body) {
        path.push(node);
        node = node.parentNode;
      }
    }
    console.log(path);

    this.scrollingChild = this.getPathContainsClass(path, 'scrollable')
  }

  getPathContainsClass(path, className){
    for (let i = 0, L= path.length; i<L; i++){
      if (path[i].classList && path[i].classList.contains(className)){
        return true;
      }
    }
    return false
  }

  //////////////////////////////////////////////////////////////////////////////////

  onTouchStart = (e) =>{
    let t = e.changedTouches[0]
    this._touchIdentifier = t.identifier
    this._touchStartY = t.clientY
    let notFirstTouch = (e.touches.length>1)
    if (notFirstTouch){
      e.preventDefault()
      e.stopImmediatePropagation()
    }

    if(!this.canLockTouch){return}
    this.checkEventPath(e);
    if (this.fbVideoFullscreen || notFirstTouch){
      if(this.bubbling(e)) {e.stopImmediatePropagation()}
    }
  }

  onTouchEnd = (e) =>{
    let t = e.changedTouches[0]
    let notFirstTouch = (t.identifier !== this._touchIdentifier)
    if (notFirstTouch){
      e.preventDefault()
      e.stopImmediatePropagation()
    }

    if(!this.canLockTouch){return}

    if (this.fbVideoFullscreen || notFirstTouch){
      if(this.bubbling(e)) {e.stopImmediatePropagation()}
      return
    }
    this._touchStartY = 0
  }

  onTouchMove = (e) =>{
    let t = e.changedTouches[0]
    let notFirstTouch = (t.identifier !== this._touchIdentifier)
    if (notFirstTouch){
      e.preventDefault()
      e.stopImmediatePropagation()
    }

    if(!this.canLockTouch){return}

    if(!this._headerDragging || notFirstTouch || this.fbVideoFullscreen ){
      if(this.bubbling(e)) {e.stopImmediatePropagation()}
     if(this.bubbling(e) && !this.scrollingChild) {e.preventDefault()}
      //e.preventDefault()
    }
  }

  onScroll = (e) =>{
    //lock scroll if fullscreen
    if(this.fbVideoFullscreen ){
      if (!this.scrolldetected){
        this.scrolldetected = true
        let max= (env.ios) ? 25 : 2; //ios scrolls to 23 while in splitscreen
        if (window.scrollY !== max) {window.scrollTo(0, max)}
        if(this.bubbling(e)) {e.stopImmediatePropagation()}
        e.preventDefault()
        this.scrolldetected = false
      }
    }
  }


  get _getOrientHeights(){return this._heights[(this.landscape || !env.mobile) ? 'landscape' :'portrait']}

  _updateContentHeight(){
    let h = this._getOrientHeights
    h.min = Math.min(Math.min(h.min , this.wh), this.perc_h)
    h.max = Math.max(Math.max(h.max , this.wh), this.vh)
  }

  _makeRulers(){
    this.el_vh = document.createElement("div")
    this.el_vh.style.cssText=`position: absolute;
                              pointer-events:none;
                              top:0px;
                              width:1px;
                              height:100vh;
                              `
    this.el_perc = document.createElement("div")
    this.el_perc.style.cssText=`position: absolute;
                                pointer-events:none;
                                top:0px;
                                width:1px;
                                height:100%;
                                `

    document.body.appendChild(this.el_vh)
    document.body.appendChild(this.el_perc)
  }

  /*--DEBUG-------------------------------------------------------------------------*/

  _updateDebugUIMessage = () =>{
    let message = `
      fb_video_ad: ${env.facebook_video_ad}
      fbVideoFullscreen: ${this.fbVideoFullscreen}
      uiMode : ${this.uiMode}
      uiSwipable : ${this.uiSwipable}
      uiShowing : ${this.uiShowing}
      uiHeight : ${this.uiHeight}

      scrollY : ${this.scrollY}
      vhHeight : ${this.vh}
      percHeight : ${this.perc_h}
      windowHeight : ${this.wh}
      screenHeight : ${this.sh}
      landscape : ${this.landscape}
      minHeight: ${this.minContentHeight}
      maxHeight: ${this.maxContentHeight}
      swipeProgressEstimate: ${this.swipeProgressEstimate.toFixed(2)}
      `

    this.infoText.innerHTML = message
  }

  _setupDebug(){
    this.info = document.createElement("div")
    this.info.style.cssText = `
      pointer-events:none;
      position: fixed;
      top: 0px;
      padding-top:100px;
      left:0px;
      color:white;
      font-size: 14px;
      width:100%;
      height:100%;
      z-index:1000000px;
    `

    this.infoText = document.createElement("div")
    this.infoText.style.cssText = `
        position: inline;
        color:cyan;
        font-size: 12px;
        text-align: right;
        white-space: pre-wrap;
        background-color:rgba(0,0,0,0.4);
        margin-right:10px;
      `

    this.info.appendChild(this.infoText)
    document.body.appendChild(this.info)
  }




}

let service = new SwipeableUIService()
export default service
